import {ReactNode, useEffect, useState} from "react";
import AuthContext from "./AuthContext";
import User from "../../api/user/User";
import UserApi from "../../api/user/UserApi";
import AuthenticationApi from "../../api/auth/AuthenticationApi";
import Storage from "../../storage/Storage";
import AuthenticationResult from "../../api/auth/AuthenticationResult";

type WrapAuthContextProps = {
    children: ReactNode;
}

function WrapAuthContext(props: WrapAuthContextProps) {
    /**
     * Holds current authenticated user.
     * @author Kacper Faber
     * @since 0.1.0
     */
    const [user, setUser] = useState<User>();

    /**
     * Called when reload method was rejected by API.
     * @author Kacper Faber
     * @since 0.1.0
     */
    const onReloadError = () => {}

    /**
     * Called when
     * @param user
     */
    const onReloadSuccess = (user: User) => {
        setUser(user);
    }

    /**
     * Reload current authenticated user in context.
     * Must be called at component mount.
     * @author Kacper Faber
     * @since 0.1.0
     */
    const reload = () => {
        UserApi.me()
            .then(onReloadSuccess)
            .catch(onReloadError);
    }

    const onLoginSuccess = ({accessToken}: AuthenticationResult) => {
        Storage.setToken(accessToken);
        reload();
    }

    /**
     * Login user with login and password.
     * Does not perform catch on promise.
     * @param login
     * @param password
     * @returns Promise<void>
     */
    const login = (login: string, password: string) =>
        AuthenticationApi.authenticate({username: login, password: password})
            .then(onLoginSuccess);

    /**
     * Logout the user.
     * @author Kacper Faber
     * @since 0.1.0
     */
    const logout = async () => {
        Storage.setToken(null);
    }

    useEffect(reload, []);

    return (
        <AuthContext.Provider
            value={{user, login, logout}}
            children={props.children}
        />
    );
}

// noinspection JSUnusedGlobalSymbols
export default WrapAuthContext;